import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { BasicButton } from '../buttons';
import Stack from '../stack/stack';
import UpgradeDialog from '../upgradeDialog/upgradeDialog';

const Controls = styled.fieldset`
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
`;

export const AgentPricingUpgradeCard: FC<{ busy: boolean; onResume: () => Promise<void> }> = ({ busy, onResume }) => {
    const { subscription, sellerMarketplace, updateSubscription } = useSubscriptionData();
    const { isAllowed } = usePermissions();
    const refresh = useRef(updateSubscription);
    refresh.current = updateSubscription;

    useEffect(() => {
        // Shopify and the subscription page may complete the upgrade outside this tab.
        const onFocus = () => refresh.current();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);

    const enabled = subscription?.isAdvancedPricingEnabled === true;
    const canUpgrade = isAllowed(['SUBSCRIPTION']);

    return (
        <Controls disabled={busy}>
            <Stack $gap='12px'>
                {enabled ? (
                    <span>{T._('Advanced Pricing is available. Resume to review the pricing configuration before applying it.', 'Admin')}</span>
                ) : canUpgrade ? (
                    <UpgradeDialog
                        type='BANNER'
                        marketPlace={sellerMarketplace ?? undefined}
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        showCloseButton={false}
                        showUpgradeButtonAsLink
                        message={
                            sellerMarketplace === 'MICROSOFT_AZURE'
                                ? T._(
                                      'Your plan does not include Advanced Pricing. Upgrade from the Zakeke SaaS app in the Azure portal, then return here. Any saved draft is kept.',
                                      'Admin'
                                  )
                                : T._(
                                      'Your plan does not include Advanced Pricing. Upgrade your plan to create or edit pricing rules. Any saved draft is kept.',
                                      'Admin'
                                  )
                        }
                        onUpgradeCompleted={async () => refresh.current()}
                    />
                ) : (
                    <span>
                        {T._(
                            'Your plan does not include Advanced Pricing. Ask the account owner or a user with subscription permissions to upgrade the plan.',
                            'Admin'
                        )}
                    </span>
                )}
                <BasicButton
                    type='button'
                    $size='small'
                    disabled={busy}
                    onClick={() => {
                        refresh.current();
                        void onResume();
                    }}
                >
                    {enabled ? T._('Resume pricing configuration', 'Admin') : T._('I upgraded my plan: check again', 'Admin')}
                </BasicButton>
            </Stack>
        </Controls>
    );
};
